import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Box,
  Grid
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DateSelector from './DateSelector';
import { PerformanceColumnSelectorModalProps } from '../../../helpers/types';
import { useColumnPeriods } from '../../../hooks/useColumnPeriods';

const PerformancePeriodsSelectorModal = ({
  open,
  onClose,
  columnPeriods: initialPeriods,
  onChange,
  dateRange
}: PerformanceColumnSelectorModalProps) => {
  const [newColumnName, setNewColumnName] = useState('');
  const [newPeriod, setNewPeriod] = useState<[string | null, string | null]>([null, null]);
  const { columnPeriods, addColumn, resetColumns, removeColumn } = useColumnPeriods({
    initialPeriods,
    dateRange
  });

  const handleAdd = () => {
    if (newPeriod[0] && newPeriod[1]) {
      addColumn(newColumnName, [newPeriod[0], newPeriod[1]]);
      setNewColumnName('');
      setNewPeriod([null, null]);
    }
  };

  const handleSave = () => {
    onChange(columnPeriods);
    onClose();
  };

  const handleCancel = () => {
    setNewColumnName('');
    setNewPeriod([null, null]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="xl" fullWidth >
      <DialogTitle>Customize Performance Periods</DialogTitle>
      <DialogContent sx={{ minHeight: '30vh' }}>
        <List>
          {columnPeriods.map(col => (
            <ListItem
              key={col.name}
              secondaryAction={
                <IconButton edge="end" aria-label={`remove ${col.name}`} onClick={() => removeColumn(col.name)}>
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemText primary={col.name} secondary={`${col.period[0]} - ${col.period[1]}`} />
            </ListItem>
          ))}
        </List>
        <Grid container spacing={2} sx={{ position: 'absolute', bottom: 0, width: '100%' }}>
          <Box display="flex" alignItems="center" mt={2} gap={1}>
            <TextField
              label="Add Period Name"
              value={newColumnName}
              onChange={e => setNewColumnName(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
            />
          </Box>
          <Box mt={2}>
            <DateSelector
              defaultRange={newPeriod}
              onDateChange={range => setNewPeriod(range)}
            />
          </Box>
          <Box display="flex" justifyContent="flex-end" mt={1}>
            <IconButton color="primary" onClick={handleAdd} aria-label="add column">
              <AddIcon />
            </IconButton>
          </Box>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={resetColumns} variant="outlined">Set to default</Button>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PerformancePeriodsSelectorModal;
